import { useEffect, useRef } from 'react'
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle'
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import FileText from 'lucide-react/dist/esm/icons/file-text'
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw'
import X from 'lucide-react/dist/esm/icons/x'

import type { PendingUpload } from '@/components/documents/DocumentRequirementCard'
import { DeleteDocumentButton } from '@/components/documents/DeleteDocumentButton'
import { Progress } from '@/components/ui/progress'
import { useDisplayUploadProgress } from '@/hooks/useDisplayUploadProgress'
import { cn, formatFileSize } from '@/lib/utils'

const SUCCESS_HOLD_MS = 450

interface UploadProgressRowProps {
  pending: PendingUpload
  onRetry: (pendingId: string) => void
  onDismiss: (pendingId: string) => void
  onCancel: (pendingId: string) => void
  onAnimationComplete: (pendingId: string) => void
  onDeleteDocument?: () => void
  isDeleting?: boolean
}

export function UploadProgressRow({
  pending,
  onRetry,
  onDismiss,
  onCancel,
  onAnimationComplete,
  onDeleteDocument,
  isDeleting = false,
}: UploadProgressRowProps) {
  const isCompleting = pending.status === 'completing'
  const displayProgress = useDisplayUploadProgress(isCompleting)
  const roundedProgress = Math.round(displayProgress)
  const showReceived = isCompleting && displayProgress >= 99.9
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    hasNotifiedRef.current = false
  }, [pending.id])

  useEffect(() => {
    if (!isCompleting || displayProgress < 99.9 || hasNotifiedRef.current) {
      return
    }

    hasNotifiedRef.current = true
    const timer = window.setTimeout(() => onAnimationComplete(pending.id), SUCCESS_HOLD_MS)
    return () => window.clearTimeout(timer)
  }, [displayProgress, isCompleting, onAnimationComplete, pending.id])

  if (pending.error) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
        <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{pending.fileName}</p>
          <p className="text-xs text-destructive">{pending.error}</p>
        </div>
        <button
          type="button"
          onClick={() => onRetry(pending.id)}
          aria-label={`Retry ${pending.fileName}`}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDismiss(pending.id)}
          aria-label={`Dismiss ${pending.fileName}`}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-xl border p-3 transition-[background-color,border-color,box-shadow] duration-500',
        isCompleting ? 'border-success-border/30 bg-success-bg/40' : 'border-border bg-muted/30',
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-500',
            isCompleting ? 'bg-white/70' : 'bg-background',
          )}
        >
          <FileText
            className={cn(
              'h-5 w-5 transition-colors duration-500',
              isCompleting ? 'text-success-border' : 'text-muted-foreground',
            )}
          />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{pending.fileName}</p>

          <div className="relative mt-1 min-h-5">
            <div
              className={cn(
                'space-y-1.5 transition-opacity duration-500',
                showReceived ? 'pointer-events-none opacity-0' : 'opacity-100',
              )}
              aria-hidden={showReceived}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">Uploading</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{roundedProgress}%</span>
              </div>
              <Progress
                value={displayProgress}
                animate={false}
                indicatorClassName={cn(
                  'transition-[background-color] duration-500',
                  isCompleting ? 'bg-success-border' : 'bg-primary',
                )}
              />
            </div>

            <p
              className={cn(
                'absolute inset-0 flex items-center gap-1 text-xs text-success-border transition-opacity duration-500',
                showReceived ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
              aria-hidden={!showReceived}
            >
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              Received
              {pending.fileSizeBytes != null ? <> · {formatFileSize(pending.fileSizeBytes)}</> : null}
            </p>
          </div>
        </div>

        {showReceived && onDeleteDocument ? (
          <DeleteDocumentButton
            fileName={pending.fileName}
            onConfirmDelete={onDeleteDocument}
            isDeleting={isDeleting}
          />
        ) : !showReceived ? (
          <button
            type="button"
            onClick={() => onCancel(pending.id)}
            aria-label={`Cancel upload of ${pending.fileName}`}
            title="Cancel upload"
            className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <div className="h-7 w-7 shrink-0" aria-hidden="true" />
        )}
      </div>
    </div>
  )
}
